import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { fmtDate } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, inputCls } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, Check, Zap, ExternalLink, Camera, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { title: "", description: "", due_date: "", priority: "normal", assigned_role: "", assigned_user_id: "", project_id: "", status: "todo" };

const MODULE_LINKS = { inventory: "/inventory", crm: "/crm", crm_lead: "/crm", absensi: "/absensi", absensi_harian: "/absensi", absensi_verify: "/absensi", cuti: "/cuti", approval: "/approvals", proyek: "/proyek", proyek_progress: "/proyek", chat: "/chat", penjualan: "/penjualan", purchase: "/purchase", dokumen: "/dokumen", kalender: "/kalender", pengajuan: "/pengajuan" };

export default function Todo() {
  const { hasPerm, projects, users: _u } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [proofTask, setProofTask] = useState(null);
  const [proofPhoto, setProofPhoto] = useState(null);
  const proofRef = useRef(null);

  const onProofPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 800 / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setProofPhoto(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  const { projectId } = useAuth();

  const load = useCallback(() => {
    const q = projectId ? `?project_id=${projectId}` : "";
    api.get(`/tasks${q}`).then((r) => setTasks(r.data));
    api.get("/chat/contacts").then((r) => setUsers(r.data)).catch(() => {});
  }, [projectId, hasPerm]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, assigned_role: form.assigned_role || null, assigned_user_id: form.assigned_user_id || null, due_date: form.due_date || null, project_id: form.project_id || null };
      if (editing) await api.put(`/tasks/${editing.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success("Tugas disimpan");
      setShowForm(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doToggle = async (t, photo = "") => {
    try {
      await api.put(`/tasks/${t.id}`, { title: t.title, description: t.description, due_date: t.due_date, priority: t.priority, assigned_role: t.assigned_role, assigned_user_id: t.assigned_user_id, project_id: t.project_id, status: t.status === "done" ? "todo" : "done", completion_photo: photo });
      if (t.status !== "done") toast.success("Tugas selesai — otomatis terhapus dari to-do list");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleDone = (t) => {
    if (t.source_module === "chat" && t.status !== "done") {
      setProofTask(t);
      setProofPhoto(null);
      return;
    }
    doToggle(t);
  };

  const submitProof = async () => {
    if (!proofPhoto) return toast.error("Bukti foto wajib diunggah");
    await doToggle(proofTask, proofPhoto);
    setProofTask(null);
    setProofPhoto(null);
  };

  const viewProof = async (t) => {
    try {
      const r = await api.get(`/tasks/${t.id}/proof`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = tasks.filter((t) => t.status !== "done");
  const projName = (id) => projects.find((p) => p.id === id)?.name;

  return (
    <div data-testid="todo-page">
      <PageHeader title="To Do List" subtitle="Tugas otomatis dari seluruh modul — selesaikan di modul terkait, tugas langsung hilang dari daftar" testid="todo-header">
        {hasPerm("todo", "create") && <Btn onClick={() => { setEditing(null); setForm({ ...emptyForm, project_id: projectId || "" }); setShowForm(true); }} data-testid="add-task-button"><Plus className="w-4 h-4" /> Tugas Baru</Btn>}
      </PageHeader>

      <p className="text-xs text-slate-500 mb-4" data-testid="todo-active-count">{shown.length} tugas aktif — tugas selesai otomatis terhapus dari daftar ini.</p>

      <div className="space-y-2" data-testid="task-list">
        {shown.length === 0 && <Card><p className="text-slate-500 text-sm">Tidak ada tugas.</p></Card>}
        {shown.map((t) => (
          <Card key={t.id} className={`!p-4 flex items-start gap-3 ${t.status === "done" ? "opacity-55" : ""} ${t.status !== "done" && t.due_date && t.due_date < new Date().toISOString().slice(0, 10) ? "!border-rose-500/60" : ""}`} testid={`task-card-${t.id}`}>
            {hasPerm("todo", "edit") && t.source_module === "chat" && (
              <button onClick={() => toggleDone(t)} data-testid={`task-toggle-${t.id}`} title="Selesaikan dengan bukti foto"
                className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${t.status === "done" ? "bg-emerald-600 border-emerald-500" : "border-slate-600 hover:border-emerald-400"}`}>
                {t.status === "done" && <Check className="w-3.5 h-3.5 text-white" />}
              </button>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {(() => { const link = MODULE_LINKS[t.source_module]; return link ? (
                  <button onClick={() => navigate(link)} className="text-left group inline-flex items-center gap-1.5" data-testid={`task-open-${t.id}`} title={`Buka modul ${t.source_module}`}>
                    <span className={`text-sm font-medium group-hover:text-emerald-300 transition-colors ${t.status === "done" ? "line-through text-slate-500" : "text-slate-100"}`}>{t.title}</span>
                    <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-emerald-400" />
                  </button>
                ) : (
                  <span className={`text-sm font-medium ${t.status === "done" ? "line-through text-slate-500" : "text-slate-100"}`}>{t.title}</span>
                ); })()}
                {t.source_module !== "manual" && <Badge tone="cyan"><Zap className="w-2.5 h-2.5 mr-1" />{t.source_module}</Badge>}
                <Badge tone={t.priority === "tinggi" ? "rose" : "slate"}>{t.priority}</Badge>
                {t.status !== "done" && t.due_date && t.due_date < new Date().toISOString().slice(0, 10) && (
                  <Badge tone="rose" data-testid={`task-overdue-${t.id}`}>TERLAMBAT — deadline terlewat</Badge>
                )}
                {t.source_module === "chat" && t.status !== "done" && <Badge tone="amber"><Camera className="w-2.5 h-2.5 mr-1" />wajib bukti foto</Badge>}
                {t.completion_photo_path && (
                  <button onClick={() => viewProof(t)} className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-400 hover:text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5" data-testid={`task-proof-${t.id}`}>
                    <ImageIcon className="w-2.5 h-2.5" /> Lihat Bukti
                  </button>
                )}
              </div>
              {t.description && <p className="text-xs text-slate-500 mt-1">{t.description}</p>}
              <div className="text-[11px] text-slate-500 mt-1.5 flex gap-3 flex-wrap">
                {t.due_date && <span>Jatuh tempo: {fmtDate(t.due_date)}</span>}
                <span>Untuk: {t.assigned_role || t.assigned_user_id || "Semua"}</span>
                {t.project_id && <span>Proyek: {projName(t.project_id) || "-"}</span>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? "Edit Tugas" : "Tugas Baru"} testid="task-form-modal">
        <div className="space-y-3">
          <Field label="Judul"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></Field>
          <Field label="Deskripsi"><textarea className={inputCls} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Jatuh Tempo"><input type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" /></Field>
            <Field label="Prioritas">
              <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-priority-select">
                <option value="rendah">Rendah</option><option value="normal">Normal</option><option value="tinggi">Tinggi</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Untuk Role">
              <select className={inputCls} value={form.assigned_role} onChange={(e) => setForm({ ...form, assigned_role: e.target.value })} data-testid="task-role-select">
                <option value="">Semua</option>
                {["Super Admin", "Direktur", "Finance", "Sales", "Purchasing", "Site Engineer", "Mandor"].map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Untuk Pengguna (opsional)">
              <select className={inputCls} value={form.assigned_user_id} onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })} data-testid="task-user-select">
                <option value="">-</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Proyek (Cost Center)">
            <select className={inputCls} value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} data-testid="task-project-select">
              <option value="">Global / Umum</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowForm(false)}>Batal</Btn>
            <Btn onClick={save} data-testid="task-save-button">Simpan</Btn>
          </div>
        </div>
      </Modal>
      <Modal open={!!proofTask} onClose={() => setProofTask(null)} title="Bukti Penyelesaian Tugas (Wajib)" testid="task-proof-modal">
        <div className="space-y-3">
          <p className="text-xs text-slate-400" data-testid="proof-modal-info">Tugas dari chat <span className="text-slate-200 font-medium">"{proofTask?.title}"</span> hanya dapat ditandai selesai dengan bukti foto.</p>
          <input ref={proofRef} type="file" accept="image/*" capture="environment" onChange={onProofPhoto} className="hidden" data-testid="proof-file-input" />
          {proofPhoto ? (
            <div>
              <img src={proofPhoto} alt="bukti" className="w-full rounded-lg border border-slate-700" data-testid="proof-preview" />
              <Btn tone="ghost" className="mt-2 !text-xs" onClick={() => { setProofPhoto(null); proofRef.current.value = ""; }}>Ulangi Foto</Btn>
            </div>
          ) : (
            <button onClick={() => proofRef.current.click()} data-testid="proof-upload-button"
              className="w-full border-2 border-dashed border-slate-700 hover:border-emerald-500/50 rounded-lg py-8 text-slate-500 hover:text-emerald-300 transition-colors flex flex-col items-center gap-2">
              <Camera className="w-6 h-6" />
              <span className="text-xs">Ambil foto / unggah bukti penyelesaian</span>
            </button>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Btn tone="ghost" onClick={() => setProofTask(null)}>Batal</Btn>
            <Btn onClick={submitProof} disabled={!proofPhoto} data-testid="proof-submit-button">Selesaikan Tugas</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
